/**
 * ============================================================================
 * AI-OS DESIGN TOKENS — Slice 8, Gate 1 (item 5)
 * ============================================================================
 *
 * SINGLE SOURCE OF TRUTH for every Slice-8 (AI-OS) surface.
 * All Gate-2/3 screens (Brain, Employees, Knowledge, Intelligence,
 * Automation, System) MUST consume these tokens — never hard-code a color,
 * spacing value, radius, or z-index inside an AI-OS component.
 *
 * Extraction source (Whapiz DNA — do NOT invent colors):
 *   - frontend/src/App.js createTheme(): brand primary #25d366 (light) /
 *     #2ee37a (dark), brand gradient 135deg #25d366→#128c7e, dark surface
 *     ladder #0B0D0C → #131614 → #1A1E1B → #1F2421, hairline borders,
 *     inset top highlight, brand focus ring.
 *   - Existing redesigned Settings surfaces (e.g. pages/AISettings) for
 *     text colors (#0f172a / #f1f5f9 …) and radii (8/10/12/14/16).
 *
 * 5-LANGUAGE-NATIVE / DIRECTION-AGNOSTIC RULE:
 *   The app ships Hebrew + Arabic (RTL) and English + Spanish + Russian
 *   (LTR) and flips `dir` at runtime (see App.js LANGUAGE_MAP). Therefore
 *   AI-OS styles must be written with LOGICAL directions only:
 *     - use marginInlineStart / paddingInlineEnd / insetInlineStart /
 *       borderStartStartRadius … — never left / right physical properties.
 *     - "start" = leading edge (right in RTL, left in LTR); "end" = trailing.
 *     - flexbox / grid are dir-aware by default — prefer them over floats
 *       or absolute left/right offsets.
 *     - icons that imply direction (arrows, chevrons) must flip with dir;
 *       neutral icons must not.
 *   Tokens themselves are dir-agnostic (colors/sizes carry no side), so a
 *   single set serves all 5 languages.
 *
 * USAGE:
 *   import { AIOS_TOKENS, aiosCssVars } from "../../aios/tokens";
 *   // 1) As CSS variables on a surface root (preferred — lets plain CSS
 *   //    and makeStyles share one vocabulary):
 *   //      <div style={aiosCssVars(theme.palette.type)}> …
 *   //    then in styles: color: "var(--aios-text-primary)".
 *   // 2) Direct JS access when a computed value is needed:
 *   //      AIOS_TOKENS.color[mode].brandPrimary
 *
 * No new dependency; plain ES module.
 * ============================================================================
 */

/** Color sets — light + dark, extracted from the live Whapiz palette. */
const color = {
  light: {
    // Brand
    brandPrimary: "#25d366", // WhatsApp green — App.js primaryColorLight default
    brandPrimaryStrong: "#128c7e", // gradient tail / selected-tab accent
    brandGradient: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    brandGradientHover: "linear-gradient(135deg, #1ebe5d 0%, #075e54 100%)",
    // Deep gradient — the approved "Business Brain OS" hero surface. Darker and
    // calmer than the brand gradient so a hero can carry white text and still
    // sit next to plain cards without shouting.
    brandGradientDeep: "linear-gradient(135deg, #0e7f66 0%, #075e54 100%)",
    heroText: "#ffffff",
    heroTextSecondary: "rgba(255,255,255,0.72)",
    brandTint: "rgba(37, 211, 102, 0.10)", // soft brand wash (progress track, hovers)
    brandTintStrong: "rgba(37, 211, 102, 0.14)", // selected states
    // Surfaces
    bg: "#fafafa", // palette.background.default
    surface1: "#ffffff", // paper / cards
    surface2: "#f8fafc", // table heads, subtle wells
    surface3: "#f1f5f9", // scrollbar track, deep wells
    // Text
    textPrimary: "#0f172a",
    textSecondary: "#64748b",
    textMuted: "#71717a",
    textOnBrand: "#ffffff",
    // Lines
    border: "#eef0f5", // paper hairline
    borderInput: "#e5e7eb", // outlined-input resting border
    divider: "#f4f4f5",
    // Effects
    focusRing: "0 0 0 3px rgba(37, 211, 102, 0.15)",
    topHighlight: "none", // dark-only signature
    shadowPopover:
      "0 10px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)",
    shadowCard: "0 4px 18px rgba(15, 23, 42, 0.04)",
    // Status (existing MUI semantic hues already used across the app)
    success: "#25d366",
    warning: "#f59e0b",
    error: "#ef4444",
    warningTint: "rgba(245, 158, 11, 0.12)",
    errorTint: "rgba(239, 68, 68, 0.10)",
    info: "#3b82f6",
  },
  dark: {
    // Brand
    brandPrimary: "#2ee37a", // App.js primaryColorDark default
    brandPrimaryStrong: "#86efac", // selected-tab accent in dark
    brandGradient: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    brandGradientHover: "linear-gradient(135deg, #1ebe5d 0%, #075e54 100%)",
    brandGradientDeep: "linear-gradient(135deg, #0e7f66 0%, #075e54 100%)",
    heroText: "#ffffff",
    heroTextSecondary: "rgba(255,255,255,0.72)",
    brandTint: "rgba(46, 227, 122, 0.10)",
    brandTintStrong: "rgba(46, 227, 122, 0.18)",
    // Surfaces — the premium-dark ladder from App.js
    bg: "#0B0D0C", // page background
    surface1: "#131614", // paper / cards (~6 L-points above bg)
    surface2: "#1A1E1B", // elevated: menus, popovers, dialogs
    surface3: "#1F2421", // input wells
    // Text
    textPrimary: "#f1f5f9",
    textSecondary: "#a8acb3",
    textMuted: "rgba(255,255,255,0.55)",
    textOnBrand: "#ffffff",
    // Lines
    border: "rgba(255,255,255,0.07)",
    borderInput: "rgba(255,255,255,0.08)",
    divider: "rgba(255,255,255,0.05)",
    // Effects
    focusRing: "0 0 0 3px rgba(46, 227, 122, 0.18)",
    topHighlight: "inset 0 1px 0 0 rgba(255,255,255,0.04)", // Linear-signature
    shadowPopover:
      "inset 0 1px 0 0 rgba(255,255,255,0.06), 0 0 0 1px rgba(255,255,255,0.06), 0 8px 16px -4px rgba(0,0,0,0.5), 0 24px 48px -12px rgba(0,0,0,0.7)",
    shadowCard: "inset 0 1px 0 0 rgba(255,255,255,0.04)",
    // Status
    success: "#2ee37a",
    warning: "#fbbf24",
    error: "#f87171",
    warningTint: "rgba(251, 191, 36, 0.14)",
    errorTint: "rgba(248, 113, 113, 0.12)",
    info: "#60a5fa",
  },
};

/** Spacing scale (px) — 4-based, matches MUI theme.spacing(n) = n*8 at the
 *  even steps. Use the named steps, not raw numbers. */
const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

/** Typography scale — mirrors the redesigned Settings surfaces
 *  (22/800 titles, 13.5/600 tabs, 11.5/700 uppercase table heads). */
const typography = {
  fontFamily: "inherit", // inherit the app font for body copy
  // The approved design pairs the app font with a monospace face for
  // "machine" text — eyebrow labels, codes and identifiers. Numbers stay in
  // the UI font but are wrapped in <bdi dir="ltr"> so RTL never reorders them.
  monoFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  // Eyebrow above a page title: uppercase, mono, wide tracking.
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.3,
    textTransform: "uppercase",
  },
  heroValue: { fontSize: 32, fontWeight: 800, lineHeight: 1.15 },
  kpiValue: { fontSize: 26, fontWeight: 800, lineHeight: 1 },
  pageTitle: { fontSize: 22, fontWeight: 800, letterSpacing: -0.3 },
  sectionTitle: { fontSize: 16, fontWeight: 700, letterSpacing: -0.2 },
  body: { fontSize: 14, fontWeight: 400 },
  bodyStrong: { fontSize: 14, fontWeight: 600 },
  caption: { fontSize: 12.5, fontWeight: 500 },
  overline: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  navItem: { fontSize: 13.5, fontWeight: 600, letterSpacing: -0.1 },
};

/** Radii (px) — the existing app ladder: chips/buttons 8, inputs 10,
 *  menus 12, paper 14, dialogs/large cards 16. */
const radius = {
  sm: 8,
  md: 10,
  lg: 12,
  xl: 14,
  xxl: 16,
  pill: 999,
};

/** Z-layers — stay UNDER MUI's fixed layers (appBar 1100, drawer 1200,
 *  modal 1300, tooltip 1500) so AI-OS chrome never covers app chrome. */
const zLayer = {
  base: 0,
  raised: 10, // cards hover states, sticky section headers
  nav: 100, // AI-OS sidebar / bottom navigation
  overlay: 1000, // in-page scrims (still under MUI appBar)
};

export const AIOS_TOKENS = { color, spacing, typography, radius, zLayer };

/**
 * Flatten one mode's tokens into a CSS-variable map suitable for a `style`
 * prop on the AI-OS surface root. All variables are prefixed `--aios-`.
 *
 * @param {"light"|"dark"} mode — pass theme.palette.type
 * @returns {Object} e.g. { "--aios-brand-primary": "#25d366", … }
 */
export function aiosCssVars(mode) {
  const c = color[mode === "dark" ? "dark" : "light"];
  const kebab = (s) => s.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  const vars = {};
  Object.keys(c).forEach((key) => {
    vars["--aios-" + kebab(key)] = c[key];
  });
  Object.keys(spacing).forEach((key) => {
    vars["--aios-space-" + key] = spacing[key] + "px";
  });
  Object.keys(radius).forEach((key) => {
    vars["--aios-radius-" + key] = radius[key] + "px";
  });
  Object.keys(zLayer).forEach((key) => {
    vars["--aios-z-" + kebab(key)] = String(zLayer[key]);
  });
  // Monospace face for eyebrows/codes — one variable so plain CSS and
  // makeStyles share it, exactly like the colors.
  vars["--aios-font-mono"] = typography.monoFamily;

  // ──────────────────────────────────────────────────────────────────────
  // DESIGN-FILE ALIASES (2026-08-03)
  //
  // The approved design source, "Business Brain OS.dc.html", names its
  // twenty-two custom properties with short keys (--tx, --bd, --grad-deep).
  // Our tokens carry the same VALUES under longer, self-documenting names
  // (textPrimary, border, brandGradientDeep). Two vocabularies for one
  // palette meant every component translated by hand, and the drift showed
  // up as "close but not the design".
  //
  // These aliases publish the design file's exact spelling alongside ours,
  // so a component can be written against the file verbatim. Purely
  // additive: every --aios-* variable above is untouched, so nothing that
  // exists today changes. Both spellings resolve to the same value, which
  // is the point — there is one palette, addressable two ways.
  //
  // Verified value-for-value against the design file on 2026-08-03; where
  // the file states a colour we emit that colour, and dark mode maps to our
  // dark ladder rather than inventing one the file never specified.
  // ──────────────────────────────────────────────────────────────────────
  vars["--brand"] = c.brandPrimary;
  vars["--brand-strong"] = c.brandPrimaryStrong;
  vars["--grad"] = c.brandGradient;
  vars["--grad-deep"] = c.brandGradientDeep;
  vars["--tint"] = c.brandTint;
  vars["--tint-strong"] = c.brandTintStrong;
  vars["--bg"] = c.bg;
  vars["--s1"] = c.surface1;
  vars["--s2"] = c.surface2;
  vars["--s3"] = c.surface3;
  vars["--tx"] = c.textPrimary;
  vars["--tx2"] = c.textSecondary;
  vars["--tx3"] = c.textMuted;
  vars["--on-brand"] = c.textOnBrand;
  vars["--bd"] = c.border;
  vars["--bdi"] = c.borderInput;
  vars["--shadow"] = c.shadowCard;
  vars["--warn"] = c.warning;
  vars["--warn-t"] = c.warningTint;
  vars["--err"] = c.error;
  vars["--err-t"] = c.errorTint;
  vars["--hero-tx2"] = c.heroTextSecondary;

  return vars;
}

/**
 * The design file's variable names, mapped to the token key each one reads.
 *
 * Exported so a check can assert the alias set stays complete — if the design
 * file gains a variable, or a token is renamed, the mismatch is findable
 * rather than silently absent from a screen.
 */
export const DESIGN_FILE_ALIASES = {
  "--brand": "brandPrimary",
  "--brand-strong": "brandPrimaryStrong",
  "--grad": "brandGradient",
  "--grad-deep": "brandGradientDeep",
  "--tint": "brandTint",
  "--tint-strong": "brandTintStrong",
  "--bg": "bg",
  "--s1": "surface1",
  "--s2": "surface2",
  "--s3": "surface3",
  "--tx": "textPrimary",
  "--tx2": "textSecondary",
  "--tx3": "textMuted",
  "--on-brand": "textOnBrand",
  "--bd": "border",
  "--bdi": "borderInput",
  "--shadow": "shadowCard",
  "--warn": "warning",
  "--warn-t": "warningTint",
  "--err": "error",
  "--err-t": "errorTint",
  "--hero-tx2": "heroTextSecondary",
};

export default AIOS_TOKENS;
